"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useLocale, useTranslations } from "next-intl";
import { ArrowUpRight } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { useIntroDone } from "@/layout/MainHero";
import Magnetic from "@/component/common/Magnetic";
import Tilt from "@/component/common/Tilt";

gsap.registerPlugin(ScrollTrigger);

/**
 * About intro. Copy on one side; on the other, the portrait in a glass frame that
 * tilts toward the pointer. The frame is the page's [data-shape-anchor], so the
 * particle globe turns behind it like a halo.
 */
export default function AboutHero() {
  const t = useTranslations("brand.about.hero");
  const ta = useTranslations("about");
  const locale = useLocale();
  const rtl = locale === "ar";
  const ready = useIntroDone();
  const root = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!ready || !root.current) return;
    const mm = gsap.matchMedia(root);
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap
        .timeline({ defaults: { ease: "expo.out" } })
        .from(".ah-line", { yPercent: 115, duration: 1.4, stagger: 0.09, delay: 0.2 })
        .from(".ah-fade", { opacity: 0, y: 18, duration: 1, stagger: 0.07 }, "-=1")
        .from(".ah-portrait", { clipPath: "inset(100% 0% 0% 0%)", duration: 1.6, ease: "expo.inOut" }, "-=1.3")
        .from(".ah-meta > *", { opacity: 0, y: 12, duration: 0.8, stagger: 0.05 }, "-=0.9");

      // slow parallax inside the portrait frame
      gsap.fromTo(
        ".ah-portrait-img",
        { yPercent: -6 },
        {
          yPercent: 6,
          ease: "none",
          scrollTrigger: { trigger: ".ah-portrait", start: "top bottom", end: "bottom top", scrub: true },
        },
      );
    });
    return () => mm.revert();
  }, [ready]);

  const meta = [
    { label: t("basedLabel"), value: t("basedValue") },
    { label: ta("focus"), value: ta("focusValue") },
    { label: t("marketsLabel"), value: t("marketsValue") },
  ];

  return (
    <section ref={root} className="relative px-5 pb-16 pt-32 md:px-10 md:pb-24 md:pt-40">
      <div className="mx-auto max-w-[1400px]">
        <div className="grid items-center gap-16 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-10">
          <div>
            <p className="ah-fade label mb-8">{t("label")}</p>
            <h1 className="display text-[clamp(2.8rem,6.6vw,7rem)] text-bone">
              <span className="block overflow-hidden pb-[0.04em]">
                <span className="ah-line block">{t("title1")}</span>
              </span>
              <span className="block overflow-hidden pb-[0.04em]">
                <span className="ah-line block">{t("title2")}</span>
              </span>
              <span className="block overflow-hidden pb-[0.1em]">
                <span className="ah-line block text-ash">{t("title3")}</span>
              </span>
            </h1>

            <p className="ah-fade mt-10 max-w-[46ch] text-[clamp(1.05rem,1.4vw,1.25rem)] leading-relaxed text-ash">
              {t("lead")}
            </p>
            <div className="ah-fade mt-10 flex flex-wrap gap-3">
              <Magnetic>
                <Link href="/projects" className="btn-solid">
                  {t("ctaWork")}
                  <ArrowUpRight size={18} className={rtl ? "-scale-x-100" : ""} />
                </Link>
              </Magnetic>
              <Magnetic>
                <Link href="/contact" className="btn-ghost">
                  {t("ctaTalk")}
                </Link>
              </Magnetic>
            </div>
          </div>

          {/* the portrait: the globe of particles orbits behind this frame */}
          <figure className="mx-auto w-full max-w-[380px] lg:me-0 lg:ms-auto" data-shape-anchor>
            <Tilt max={6} className="rounded-[28px]">
              <div className="ah-portrait relative aspect-[4/5] overflow-hidden rounded-[28px] border border-[var(--hair-strong)] bg-void-2 shadow-[0_50px_120px_-40px_rgba(0,0,0,0.95)]">
                <div className="ah-portrait-img absolute inset-[-7%_0]">
                  <Image
                    src="/about.png"
                    alt={t("portraitAlt")}
                    fill
                    priority
                    sizes="(min-width: 1024px) 380px, 90vw"
                    className="object-cover grayscale"
                  />
                </div>
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-void/85 via-void/10 to-transparent" />
                <figcaption className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5">
                  <span className="flex flex-col gap-1">
                    <span className="font-display text-lg font-bold tracking-[-0.02em] text-bone">{t("caption")}</span>
                    <span className="label">{t("basedValue")}</span>
                  </span>
                  <span className="tabular font-mono text-[11px] text-heat" dir="ltr">
                    30.04°N
                    <br />
                    31.24°E
                  </span>
                </figcaption>
              </div>
            </Tilt>
          </figure>
        </div>

        {/* meta row */}
        <dl className="ah-meta mt-20 grid grid-cols-1 gap-x-6 gap-y-6 border-t border-[var(--hair)] pt-6 sm:grid-cols-2 md:mt-28 lg:grid-cols-4">
          {meta.map((m) => (
            <div key={m.label} className="flex flex-col gap-1.5">
              <dt className="label">{m.label}</dt>
              <dd className="text-[15px] leading-snug text-bone">{m.value}</dd>
            </div>
          ))}
          <div className="flex flex-col gap-1.5">
            <dt className="label">{t("statusLabel")}</dt>
            <dd className="flex items-center gap-2.5 text-[15px] leading-snug text-bone">
              <span aria-hidden="true" className="live-dot" />
              {t("statusValue")}
            </dd>
          </div>
        </dl>
      </div>
    </section>
  );
}
